import React from 'react';
import { Card } from '../common/Card';

interface V6ResultDisplayProps {
  result: any;
  bibsTeam: 'A' | 'B';
  onCopyTeams: () => void;
  onSaveMatch: () => void;
}

export function V6ResultDisplay({ result, bibsTeam, onCopyTeams, onSaveMatch }: V6ResultDisplayProps) {
  if (!result) return null;

  const teamAName = bibsTeam === 'A' ? 'TEAM A (bibs)' : 'TEAM A';
  const teamBName = bibsTeam === 'B' ? 'TEAM B (bibs)' : 'TEAM B';

  const calibration = result.calibration || {};
  const isCalibrated = calibration.is_calibrated;
  const confidence = calibration.confidence || 0;
  const matchesAnalyzed = calibration.matches_analyzed || 0;

  return (
    <div className="space-y-6">
      {/* Calibration Status Banner */}
      {!isCalibrated && (
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <div className="text-2xl">🔧</div>
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                Algorithm V6: Learning Mode
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                Using default weights. After 10+ matches with scores, V6 will automatically calibrate based on what wins matches in your league.
              </p>
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Matches needed:</strong> 10 | <strong>Current:</strong> {matchesAnalyzed} | <strong>Remaining:</strong> {10 - matchesAnalyzed}
              </div>
            </div>
          </div>
        </Card>
      )}

      {isCalibrated && (
        <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <div className="flex items-start gap-3">
            <div className="text-2xl">✅</div>
            <div className="flex-1">
              <h3 className="font-semibold text-green-900 dark:text-green-100 mb-1">
                Algorithm V6: Calibrated!
              </h3>
              <p className="text-sm text-green-800 dark:text-green-200 mb-2">
                Learned from {matchesAnalyzed} matches | Confidence: {Math.round(confidence * 100)}%
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                <strong>League Style:</strong> {calibration.league_style || 'Balanced'}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Teams Display */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Team A */}
        <Card>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {teamAName}
          </h2>
          <div className="space-y-2">
            {result.team_a?.map((player: any, idx: number) => (
              <div
                key={idx}
                className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800 rounded"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">{player.name}</span>
                  {player.is_gk_specialist && (
                    <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 px-2 py-0.5 rounded">
                      🧤 GK
                    </span>
                  )}
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {player.contribution_score?.toFixed(1) || player.overall_rating?.toFixed(1)}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Contribution Total:</span>
                <span className="font-semibold">{result.contribution?.team_a?.total?.toFixed(1)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Chemistry:</span>
                <span className="font-semibold">{result.chemistry?.team_a?.toFixed(1)}/10</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Team B */}
        <Card>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {teamBName}
          </h2>
          <div className="space-y-2">
            {result.team_b?.map((player: any, idx: number) => (
              <div
                key={idx}
                className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800 rounded"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">{player.name}</span>
                  {player.is_gk_specialist && (
                    <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 px-2 py-0.5 rounded">
                      🧤 GK
                    </span>
                  )}
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {player.contribution_score?.toFixed(1) || player.overall_rating?.toFixed(1)}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Contribution Total:</span>
                <span className="font-semibold">{result.contribution?.team_b?.total?.toFixed(1)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Chemistry:</span>
                <span className="font-semibold">{result.chemistry?.team_b?.toFixed(1)}/10</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Balance Analysis */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Balance Analysis
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {result.balance?.contribution_difference?.toFixed(1)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Contribution Diff
            </div>
          </div>
          <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {result.balance?.chemistry_difference?.toFixed(1)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Chemistry Diff
            </div>
          </div>
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {result.balance?.quality || 'GOOD'}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Balance Quality
            </div>
          </div>
        </div>

        {/* Goalkeeper Info */}
        {result.goalkeeping && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {result.goalkeeping.explanation}
            </p>
          </div>
        )}
      </Card>

      {/* Actions */}
      <div className="flex gap-4">
        <button
          onClick={onCopyTeams}
          className="flex-1 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
        >
          Copy Teams
        </button>
        <button
          onClick={onSaveMatch}
          className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
        >
          Save to History
        </button>
      </div>

      {/* Chemistry & Variety Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Chemistry Breakdown */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            ⚡ Chemistry Breakdown
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Team A Chemistry</span>
              <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                {result.chemistry?.team_a?.toFixed(1) || '0.0'}/10
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Team B Chemistry</span>
              <span className="text-lg font-bold text-red-600 dark:text-red-400">
                {result.chemistry?.team_b?.toFixed(1) || '0.0'}/10
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Difference</span>
              <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
                {result.balance?.chemistry_difference?.toFixed(2) || '0.00'}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Based on historical match pairings. Higher = players who often play together.
            </div>
          </div>
        </Card>

        {/* Variety Breakdown */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            🎲 Variety Score
          </h3>
          <div className="space-y-3">
            <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg">
              <span className="text-4xl font-bold text-green-600 dark:text-green-400">
                {result.variety?.score?.toFixed(1) || '0.0'}
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400 mt-2">out of 10</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Minimizes repetitive pairings from recent matches. Higher score = fresher team combinations with less familiar pairings.
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 p-2 rounded">
              <strong>How it works:</strong> The algorithm tracks player pairings from recent matches and penalizes combinations that have been used frequently, ensuring varied and interesting matchups.
            </div>
          </div>
        </Card>
      </div>

      {/* Algorithm Info */}
      {isCalibrated && calibration.category_weights && (
        <Card>
          <details className="cursor-pointer">
            <summary className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
              📊 Algorithm Insights (Click to expand)
            </summary>
            <div className="mt-4 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Category weights learned from your league's matches:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div className="flex justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                  <span>🏃 Mobility:</span>
                  <span className="font-semibold">{Math.round((calibration.category_weights.mobility || 0) * 100)}%</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                  <span>⚽ Ball Skill:</span>
                  <span className="font-semibold">{Math.round((calibration.category_weights.ballSkill || 0) * 100)}%</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                  <span>🧠 Intelligence:</span>
                  <span className="font-semibold">{Math.round((calibration.category_weights.intelligence || 0) * 100)}%</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                  <span>🎯 Attacking:</span>
                  <span className="font-semibold">{Math.round((calibration.category_weights.attacking || 0) * 100)}%</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                  <span>🛡️ Defending:</span>
                  <span className="font-semibold">{Math.round((calibration.category_weights.defending || 0) * 100)}%</span>
                </div>
                <div className="flex justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                  <span>🤝 Teamplay:</span>
                  <span className="font-semibold">{Math.round((calibration.category_weights.teamplay || 0) * 100)}%</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                Generation time: {result.metadata?.generation_time_ms}ms | Iterations: {result.metadata?.iterations?.toLocaleString()}
              </p>
            </div>
          </details>
        </Card>
      )}
    </div>
  );
}
